import type { FC } from "react";
import { Box, Card, Skeleton, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBriefcase,
  faUsers,
  faCircleCheck,
  faQuestion,
} from "@fortawesome/free-solid-svg-icons";

export interface JobStatsItemProps {
  activeOpenings: number;
  applications: number;
  shortlisted: number;
  questionAnswered: number;
}

interface JobStatsGridProps {
  loading?: boolean;
  item: JobStatsItemProps;
}

const DARK_CARD_BG = "#121824";
const ICON_BG = "#161d2b";
const DARK_BORDER = "#1e2638";

const JobStatsGrid: FC<JobStatsGridProps> = ({ loading = false, item }) => {
  const stats = [
    {
      title: "Active openings",
      value: item.activeOpenings,
      icon: faBriefcase,
    },
    {
      title: "Applications",
      value: item.applications,
      icon: faUsers,
    },
    {
      title: "Shortlisted",
      value: item.shortlisted,
      icon: faCircleCheck,
    },
    {
      title: "Questions answered",
      value: item.questionAnswered,
      icon: faQuestion,
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#0b0f17", // Force le fond sombre unifié sans cadre blanc
        p: 0,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
          },
          gap: 2.5,
        }}
      >
        {stats.map((stat, index) => (
          <Card
            key={index}
            variant="outlined"
            sx={{
              backgroundColor: DARK_CARD_BG,
              borderColor: DARK_BORDER,
              borderRadius: "14px",
              p: 2.5,
              boxShadow: "none",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Conteneur d'icône */}
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "10px",
                backgroundColor: ICON_BG,
                border: `1px solid ${DARK_BORDER}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#8c94a4",
                flexShrink: 0,
              }}
            >
              <FontAwesomeIcon icon={stat.icon} style={{ fontSize: "18px" }} />
            </Box>

            {/* Informations textes */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {loading ? (
                <>
                  <Skeleton variant="text" width={40} height={32} sx={{ bgcolor: "#1e2638" }} />
                  <Skeleton variant="text" width={90} height={20} sx={{ bgcolor: "#1e2638" }} />
                </>
              ) : (
                <>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: "#ffffff",
                      lineHeight: 1.1,
                      mb: 0.5,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#8c94a4",
                      fontSize: "0.85rem",
                      fontWeight: 500,
                    }}
                  >
                    {stat.title}
                  </Typography>
                </>
              )}
            </Box>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default JobStatsGrid;